<template>
  <div class="booking-details" v-if="record">
    <div class="d-flex justify-content-between"><h2>Booking Details</h2><h3 v-if="record.RefCode">{{ record.RefCode }}</h3></div>
    <div v-if="lastUpdated" class="last-updated">Última actualización: {{ lastUpdated }}</div>

    <!-- Contact Information -->
    <section class="section" v-if="!isGroup">
      <h3>Contact Information</h3>
      <div class="details-container">
        <div class="detail-item">
          <span class="label">Name:</span>
          <span class="value">{{ booking.PaxName }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Address:</span>
          <span class="value">{{ booking.Address }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Zip Code:</span>
          <span class="value">{{ booking.ZipCode }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Nationality:</span>
          <span class="value">{{ booking.Nationality?.toUpperCase() }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Mobile:</span>
          <span class="value">{{ booking.Mobile }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Email:</span>
          <span class="value">{{ booking.Email }}</span>
        </div>
      </div>
    </section>

    <!-- Emergency Contact Information -->
    <section class="section">
      <h3>Emergency Contact</h3>
      <div class="details-container">
        <div class="detail-item">
          <span class="label">Full name:</span>
          <span class="value">{{ booking.EmergencyContactFullName }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Relationship:</span>
          <span class="value">{{ booking.EmergencyContactRelationship }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Main telephone number with country/area code:</span>
          <span class="value">{{ booking.EmergencyContactPrimaryPhone }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Secondary phone:</span>
          <span class="value">{{ booking.EmergencyContactSecondaryPhone }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Email address:</span>
          <span class="value">{{ booking.EmergencyContactEmail }}</span>
        </div>
      </div>
    </section>

    <!-- Passengers -->
    <section class="section" v-if="booking.BookingFormPaxs?.length">
      <h3>Passengers</h3>
      <div class="details-container" v-for="(pax, index) in booking.BookingFormPaxs" :key="index">
        <h4 v-if="pax.Selected">{{ pax.Selected }}</h4>
        <h4 v-else>Passenger {{ index + 1 }}</h4>
        <template v-if="isGroup">
          <div class="detail-item" v-for="field in groupPassengerFields" :key="field.key">
            <span class="label">{{ field.label }}:</span>
            <span class="value">{{ groupPaxValue(pax, field) }}</span>
          </div>
          <div class="medical-info">
            <h5>Medical Coverage / Travel Insurance</h5>
            <div class="detail-item"><span class="label">Insurance company:</span><span class="value">{{ pax.InsuranceCompany || '' }}</span></div>
            <div class="detail-item"><span class="label">Policy number:</span><span class="value">{{ pax.InsurancePolicyNumber || '' }}</span></div>
            <div class="detail-item"><span class="label">24/7 international assistance / emergency phone:</span><span class="value">{{ pax.InsuranceEmergencyPhone || '' }}</span></div>
          </div>
        </template>
        <template v-else>
        <div class="detail-item">
          <span class="label">Selected Passenger:</span>
          <span class="value">{{ pax.Selected }}</span>
        </div>
        <div class="detail-item">
          <span class="label">First Name:</span>
          <span class="value">{{ pax.FirstName }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Last Name:</span>
          <span class="value">{{ pax.LastName }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Date of Birth:</span>
          <span class="value">{{ pax.DOB }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Gender:</span>
          <span class="value">{{ pax.Gender }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Passport:</span>
          <span class="value">{{ pax.Passport }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Passport Expiration Date:</span>
          <span class="value">{{ pax.PassportExpirationDate }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Nationality:</span>
          <span class="value">{{ pax.Nationality?.toUpperCase() }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Trip Reason:</span>
          <span class="value">{{ pax.TripReason }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Mobile:</span>
          <span class="value">{{ pax.Mobile }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Email:</span>
          <span class="value">{{ pax.Email }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Emergency Contact:</span>
          <span class="value">{{ pax.EmergencyContact }}</span>
        </div>
        <div class="medical-info">
          <h5>Medical Coverage / Travel Insurance</h5>
          <div class="detail-item"><span class="label">Insurance company:</span><span class="value">{{ pax.InsuranceCompany || '' }}</span></div>
          <div class="detail-item"><span class="label">Policy number:</span><span class="value">{{ pax.InsurancePolicyNumber || '' }}</span></div>
          <div class="detail-item"><span class="label">24/7 international assistance / emergency phone:</span><span class="value">{{ pax.InsuranceEmergencyPhone || '' }}</span></div>
        </div>
        <div class="detail-item" v-if="pax.ArrivalFlight">
          <span class="label">Arrival Flight:</span>
          <span class="value">{{ pax.ArrivalFlight }}</span>
        </div>
        <div class="detail-item" v-if="pax.DepartureFlight">
          <span class="label">Departure Flight:</span>
          <span class="value">{{ pax.DepartureFlight }}</span>
        </div>
        <div class="medical-info">
          <h5>Medical Information</h5>
          <div class="detail-item">
            <span class="label">Allergies:</span>
            <span class="value">{{ pax.Allergies || '' }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Medical Conditions / Medications:</span>
            <span class="value">{{ pax.MedicalConditionsMedications || [pax.MedicalConditions, pax.Medication].filter(Boolean).join(' / ') || '' }}</span>
          </div>
          <div class="detail-item">
            <span class="label">Food Restrictions:</span>
            <span class="value">{{ pax.FoodRestriction || '' }}</span>
          </div>
        </div>
        <div v-if="pax.BookingFormFlights?.length" class="medical-info">
          <h5>Passenger Flights</h5>
          <div v-for="(flight, flightIndex) in pax.BookingFormFlights" :key="flightIndex" class="detail-item">
            <span class="label">{{ flight.FlightType }}:</span>
            <span class="value">
              {{ flight.Airline }} {{ flight.FlightNumber }} —
              {{ flight.DepartureCity }} {{ formatDateTime(flight.DepartureDate, flight.DepartureTime) }} →
              {{ flight.ArrivalCity }} {{ formatDateTime(flight.ArrivalDate, flight.ArrivalTime) }}
              <template v-if="flight.ReservationCode"> ({{ flight.ReservationCode }})</template>
            </span>
          </div>
        </div>
        </template>
      </div>
      <button class="btn btn-secondary mt-3" @click="downloadPaxCSV">Descargar pasajeros en csv</button>
    </section>

    <!-- Flights -->
    <section class="section" v-if="booking.BookingFormFlights?.length">
      <h3>Flight Information</h3>
      <div class="details-container" v-for="(flight, index) in booking.BookingFormFlights" :key="index">
        <h4>Flight {{ index + 1 }} ({{ flight.FlightType }})</h4>
        <div class="detail-item">
          <span class="label">Airline:</span>
          <span class="value">{{ flight.Airline }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Flight Number:</span>
          <span class="value">{{ flight.FlightNumber }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Reservation Code:</span>
          <span class="value">{{ flight.ReservationCode }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Departure:</span>
          <span class="value">{{ flight.DepartureCity }} - {{ formatDateTime(flight.DepartureDate, flight.DepartureTime) }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Arrival:</span>
          <span class="value">{{ flight.ArrivalCity }} - {{ formatDateTime(flight.ArrivalDate, flight.ArrivalTime) }}</span>
        </div>
      </div>
    </section>

    <!-- Rooms -->
    <section class="section" v-if="!isGroup && booking.BookingFormRooms?.length">
      <h3>Room Information</h3>
      <div class="details-container" v-for="(room, index) in booking.BookingFormRooms" :key="index">
        <h4>Room {{ index + 1 }}</h4>
        <div class="detail-item">
          <span class="label">Type:</span>
          <span class="value">{{ room.Type }}</span>
        </div>
        <div class="detail-item">
          <span class="label">Names:</span>
          <span class="value">{{ room.Names }}</span>
        </div>
      </div>
    </section>

    <section class="section" v-if="booking.SuggestedHotels">
      <h3>Hotel Preferences</h3>
      <div class="details-container">
        <div class="detail-item">
          <span class="value">{{ booking.SuggestedHotels }}</span>
        </div>
      </div>
    </section>

    <section class="section row">
      <div class="form-group col-lg-4 col-4">
        <div class="form-group form-default">
          <label class="float-label ml-1 fill">Ref Secundario</label>
          <input class="form-control" type="text" v-model="record.SecondaryRefCode"/>
          <button class="btn btn-primary mt-4" @click="updateSecondaryRefCode" :disabled="!record.SecondaryRefCode">Actualizar Ref Secundario</button>
          <span class="form-bar">
          </span>
        </div>
      </div>
      <button v-if="canResetBookingForm" class="btn btn-danger" @click="openResetModal">
        Editar / eliminar datos
      </button>
    </section>

    <section class="section" v-if="attachmentData.length">
      <h3>Attachments</h3>
      <div class="attachments-container relative">
        <div 
          v-for="(file, index) in attachmentData" 
          :key="index" 
          class="attachment-card" 
         
        >
          <div class="file-icon absolute top-px right-px" @click="deleteFile(file.key)">
            <i class="x ">x</i>
          </div>
          <div class="file-name"  @click="downloadFile(file.key, file.file_name.split('.')[0])">
            {{ file.file_name }}
          </div>
        </div>
      </div>
    </section>
    <BookingFormEditModal
      v-if="showResetModal"
      :booking="booking"
      :attachments="attachmentData"
      :saving="resetting"
      @close="closeResetModal"
      @save="saveBookingFormChanges"
    />
  </div>
</template>

<script>
  /* global $, tr, frontTools */
  const api = window.api;
  import moment from 'moment';
  import RecordMixin from '@/components/tools/RecordMixin.vue';
  import BookingFormEditModal from './BookingFormEditModal.vue';
  export default {
    name: 'BookingDetails',
    mixins: [RecordMixin],
    components: { BookingFormEditModal },
    props: ['id'],
    async mounted () {
      try {
        await this.getExtraData()
        if (this.record) {
          this.refSecondary = this.record.SecondaryRefCode;
        }
      }
      catch (err) {
        console.log(err);
      }
    },
    data() {
      return {
        table: 'bookingform',
        attachmentData: [],
        refSecondary: '',
        showResetModal: false,
        resetting: false,
      };
    },
    computed: {
      booking() {
        return this.record;
      },
      lastUpdated() {
        if (!this.booking) return '';

        const passengers = Array.isArray(this.booking.BookingFormPaxs) ? this.booking.BookingFormPaxs : [];
        const relatedRecords = [
          this.booking,
          ...passengers,
          ...(Array.isArray(this.booking.BookingFormFlights) ? this.booking.BookingFormFlights : []),
          ...(Array.isArray(this.booking.BookingFormRooms) ? this.booking.BookingFormRooms : []),
          ...passengers.reduce((flights, pax) => {
            if (Array.isArray(pax.BookingFormFlights)) flights.push(...pax.BookingFormFlights);
            return flights;
          }, []),
        ];
        const modifiedDates = relatedRecords
          .map(record => moment.utc(record && record.ModifiedUTC))
          .filter(date => date.isValid());

        if (!modifiedDates.length) return '';
        return moment.max(modifiedDates).local().format('DD/MM/YYYY HH:mm');
      },
      isGroup() {
        if ((this.booking?.RefCode || '').toUpperCase().startsWith('GR')) return true;
        return false;
        // return (this.booking?.BookingFormPaxs || []).some(pax => String(pax.MiddleName || '').trim());
      },
      canResetBookingForm() {
        const user = this.$store.state.user || api.currentUser;
        const accessGroup = user && user.AccessGroup;
        if (!user || !accessGroup) return false;
        const permissions = accessGroup && (accessGroup.permissionsMerge || accessGroup.permissions);
        const endpointPermission = permissions && permissions.api && permissions.api['/api/tpsql/reset_bookingform'];
        if (!endpointPermission || !endpointPermission.methods || endpointPermission.methods.POST === undefined) return true;
        return endpointPermission.methods.POST !== false;
      },
      groupPassengerFields() {
        return [
          { key: 'FirstName', label: 'First Name' },
          { key: 'MiddleName', label: 'Middle Name' },
          { key: 'LastName', label: 'Last Name' },
          { key: 'Nationality', label: 'Nationality', uppercase: true },
          { key: 'DOB', label: 'Date of Birth' },
          { key: 'Passport', label: 'Passport' },
          { key: 'PassportExpirationDate', label: 'Passport Expiration Date' },
          { key: 'Email', label: 'Email' },
          { key: 'Mobile', label: 'Mobile' },
          { key: 'EmergencyContact', label: 'Emergency Contact' },
          { key: 'Allergies', label: 'Allergies' },
          { key: 'FoodRestriction', label: 'Food Restrictions' },
          { key: 'MedicalConditionsMedications', label: 'Medical Condition & Medication' },
          { key: 'SharingRoomWith', label: 'Sharing Room With' },
          { key: 'TypeOfBedding', label: 'Type of Bedding' },
        ];
      }
    },
    methods: {
      openResetModal() {
        this.showResetModal = true;
      },
      closeResetModal() {
        if (!this.resetting) this.showResetModal = false;
      },
      async saveBookingFormChanges(payload) {
        this.resetting = true;
        try {
          const response = await api.save(`/api/tpsql/reset_bookingform/${this.id}`, payload, (error) => {
            this.$root.$refs.index.showNotification(error, 'danger');
          });
          if (!response) return;
          await this.fetchRecord();
          await this.getExtraData();
          this.showResetModal = false;
          this.$root.$refs.index.showNotification('Booking Form actualizado correctamente.', 'success');
        } catch (error) {
          console.error('Error al eliminar datos del Booking Form:', error);
        } finally {
          this.resetting = false;
        }
      },
      groupPaxValue(pax, field) {
        let value = pax[field.key] || '';
        if (field.key === 'MedicalConditionsMedications' && !value) {
          value = [pax.MedicalConditions, pax.Medication].filter(Boolean).join(' / ');
        }
        return field.uppercase ? value.toUpperCase() : value;
      },
      formatDateTime(date, time) {
        if (!date) return '';
        const [ year, month, day ] = date.split('-');
        const formattedDate = `${day}/${month}/${year}`;
        return time ? `${formattedDate} ${time}` : formattedDate;
      },
      async getExtraData (){
        const response = await api.get(`/api/tpsql/list_booking_files/${this.id}`, null, (error) => {
          this.$root.$refs.index.showNotification(error, 'danger')
        });
        this.attachmentData = response.attachments || [];
      },
      async downloadFile(key, fileName) {
        try {
          await api.downloadFile('/api/tpsql/get_attach', { key }, fileName);
        } catch (error) {
          console.error('Error al descargar el archivo:', error);
          alert('Hubo un error al intentar descargar el archivo.');
        }
      },
      async deleteFile (keyattach) {
        const response = await api.remove(`/api/tpsql/delete_attach?key=${keyattach}`, null, (error) => {
          this.$root.$refs.index.showNotification(error, 'danger')
        });
        if (response) {
          this.attachmentData = this.attachmentData.filter(file => file.key !== keyattach);
        }
      },
      async updateSecondaryRefCode() {
        await api.save(`/api/tpsql/update_secondary_ref_code/${this.id}`, { secondary_ref_code: this.record.SecondaryRefCode }, (err) => { console.log(err)});
      },
      sharedBookingCSVValues() {
        return [
          this.booking.PaxName || '',
          this.booking.Address || '',
          this.booking.ZipCode || '',
          this.booking.Nationality || '',
          this.booking.Mobile || '',
          this.booking.Email || '',
          this.booking.EmergencyContactFullName || '',
          this.booking.EmergencyContactRelationship || '',
          this.booking.EmergencyContactPrimaryPhone || '',
          this.booking.EmergencyContactSecondaryPhone || '',
          this.booking.EmergencyContactEmail || ''
        ];
      },
      downloadPaxCSV() {
        const sharedHeaders = [
          'RESERVATION NAME', 'ADDRESS', 'ZIP CODE', 'CONTACT NATIONALITY', 'CONTACT MOBILE',
          'CONTACT EMAIL', 'EMERGENCY CONTACT FULL NAME', 'EMERGENCY CONTACT RELATIONSHIP',
          'EMERGENCY CONTACT PRIMARY PHONE', 'EMERGENCY CONTACT SECONDARY PHONE',
          'EMERGENCY CONTACT EMAIL'
        ];
        const fitHeaders = [
          ...sharedHeaders,
          'NAME', 'LAST NAME', 'EMAIL', 'PASSPORT NUMBER', 'NATIONALITY',
          'DATE OF BIRTH (DD/MM/YYYY)', 'Allergies & Food restrictions',
          'Medical Conditions / Medications',
          'Arrival Flight (Flight number, airports, arrival time & booking code)',
          'Departure Flight (Flight number, airports, departure time & booking code)',
          'Emergency Contact', 'INSURANCE COMPANY', 'INSURANCE POLICY NUMBER', 'INSURANCE EMERGENCY PHONE'
        ];

        const fitRows = this.booking.BookingFormPaxs.map(pax => [
          ...this.sharedBookingCSVValues(),
          pax.FirstName || '',
          pax.LastName || '',
          pax.Email || '',
          pax.Passport || '',
          pax.Nationality || '',
          pax.DOB || '',
          [pax.Allergies, pax.FoodRestriction].filter(Boolean).join(', '),
          pax.MedicalConditionsMedications || [pax.MedicalConditions, pax.Medication].filter(Boolean).join(' / ') || '',
          pax.ArrivalFlight || '',
          pax.DepartureFlight || '',
          pax.EmergencyContact || '', pax.InsuranceCompany || '', pax.InsurancePolicyNumber || '',
          pax.InsuranceEmergencyPhone || ''
        ]);
        const groupHeaders = [
          ...sharedHeaders,
          'FIRST NAME', 'MIDDLE NAME', 'LAST NAME', 'NATIONALITY', 'DATE OF BIRTH',
          'PASSPORT', 'PASSPORT EXPIRATION DATE', 'EMAIL', 'MOBILE', 'EMERGENCY CONTACT',
          'ALLERGIES', 'FOOD RESTRICTIONS', 'MEDICAL CONDITION & MEDICATION',
          'SHARING ROOM WITH', 'TYPE OF BEDDING', 'INSURANCE COMPANY',
          'INSURANCE POLICY NUMBER', 'INSURANCE EMERGENCY PHONE'
        ];
        const groupRows = this.booking.BookingFormPaxs.map(pax => [
          ...this.sharedBookingCSVValues(),
          pax.FirstName || '', pax.MiddleName || '', pax.LastName || '', pax.Nationality || '',
          pax.DOB || '', pax.Passport || '', pax.PassportExpirationDate || '', pax.Email || '',
          pax.Mobile || '', pax.EmergencyContact || '', pax.Allergies || '', pax.FoodRestriction || '',
          pax.MedicalConditionsMedications || [pax.MedicalConditions, pax.Medication].filter(Boolean).join(' / ') || '',
          pax.SharingRoomWith || '', pax.TypeOfBedding || '', pax.InsuranceCompany || '',
          pax.InsurancePolicyNumber || '', pax.InsuranceEmergencyPhone || ''
        ]);
        const headers = this.isGroup ? groupHeaders : fitHeaders;
        const rows = this.isGroup ? groupRows : fitRows;

        const csvContent = [headers, ...rows]
          .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
          .join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `passengers_${this.booking.RefCode || 'booking'}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      },
      downloadCSV () {
        api.setProcessing(true);
        this.options.perPage = this.dataList.length;
        setTimeout(() => {
            api.setProcessing(false);
        }, 1);
          this.downloadInterval = setInterval(() => {
              let table = $('#data-table');
              if (table.length > 0) {
                  if (this.currentSort && this.$refs.clientTable) {
                    this.$refs.clientTable.setOrder(this.currentSort.column, this.currentSort.ascending);
                  }
                  let fileName = 'data.csv'
                  if (this.title) fileName = tr(this.title) + '.csv';
                  if (this.getFileName) {
                      fileName = this.getFileName('csv')
                  }
                  frontTools.downloadReportCSV('data-table', fileName);
                  if (this.afterDownload) this.afterDownload();
                  clearInterval(this.downloadInterval);
              }
          }, 200);
      },
    }
  }
</script>

<style scoped>
.booking-details {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;
}

.section {
  margin-bottom: 30px;
}

.last-updated {
  color: #888;
  font-size: 0.8rem;
  margin-top: -8px;
  margin-bottom: 12px;
}

.booking-header {
  gap: 15px;
  margin-bottom: 20px;
}

.details-container {
  background: #f5f5f5;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 15px;
}

.detail-item {
  margin-bottom: 12px;
  display: flex;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.label {
  font-weight: bold;
  min-width: 150px;
  color: #333;
}

.value {
  color: #666;
}

h3 {
  color: #2c3e50;
  margin-bottom: 15px;
}

h4 {
  color: #2c3e50;
  margin-bottom: 12px;
}

.medical-info {
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #ddd;
}

h5 {
  color: #2c3e50;
  margin-bottom: 10px;
}

.attachments-container {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.attachment-card {
  background: #f5f5f5;
  padding: 10px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: background 0.3s;
}

.attachment-card:hover {
  background: #e0e0e0;
}

.file-icon {
  margin-right: 10px;
}

.file-name {
  color: #333;
}

.text-right {
  text-align: right !important;
}
.text-left {
  text-align: left !important;
}
.bold {
  font-weight: bold !important;
}

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
